import { Q2_PRO_REFERENCE_VIDEO_MAX_DURATION } from '@/app/constants';
import type { CreationInfo, TaskInfo } from '@/app/types';
import { isAdmissionTemplateTask, isSceneTask, isVideoDurationWithinRange } from '@/app/utils';
import { ActionButtonType } from '../types';

/**
 * 判断是否为失败或取消任务
 * @param task
 * @returns
 */
export const isFailedOrCanceledTask = (task: TaskInfo) => {
  return task.state === 'failed' || task.state === 'canceled';
};

/**
 * 音频任务按钮配置
 */
export const getAudioButtonConfig = (options: { creation?: CreationInfo; task: TaskInfo }): ActionButtonType[] => {
  const { creation, task } = options;

  // 音频每次都只生成一个，所以如果失败或取消或者审核失败，则展示 ellipsis 菜单
  if (isFailedOrCanceledTask(task) || creation?.err_info?.err_code) {
    return [ActionButtonType.ELLIPSIS_MENU];
  }

  return [
    ActionButtonType.DOWNLOAD,
    ActionButtonType.SHARE,
    ActionButtonType.ELLIPSIS_MENU,
    ActionButtonType.STAR, // 音频任务不支持投稿、超分、截帧、图片应用
  ];
};

/**
 * 视频任务按钮配置
 */
export const getVideoButtonConfig = (options: {
  task: TaskInfo;
  creation?: CreationInfo;
  isLgScreen: boolean;
  type: 'task' | 'creation';
}): ActionButtonType[] => {
  const { task, creation, isLgScreen, type } = options;

  // 生成物上的展示，在失败任务或者没有 creation 都不展示按钮，如果生成物有错误，则展示 ellipsis 菜单
  if (type === 'creation') {
    if (isFailedOrCanceledTask(task) || !creation) {
      return [];
    }

    // 只会出现在 taskCard 下面，而不会出现在 creationCard 上面
    if (creation.err_info?.err_code) {
      return [ActionButtonType.ELLIPSIS_MENU];
    }
  } else {
    // 在任务上展示，在失败任务或者生成物有错误，则展示 ellipsis 菜单
    if (isFailedOrCanceledTask(task) || !creation || creation?.err_info?.err_code) {
      return [ActionButtonType.ELLIPSIS_MENU];
    }
  }

  const creationCardCount = task.settings?.sample_count ?? 1;
  const resolution = task.settings?.resolution;
  const canSubmitCreation = task.extra_params?.is_my_voice !== true && !isAdmissionTemplateTask(task);

  // 如果卡片数量大于1，或者卡片数量为1且是移动端，则显示投稿按钮
  return [
    // 视频快速编辑：只有时长 <= Q2 Pro 参考生视频最大时长的视频才显示，music_video 不支持
    ...(creation.type === 'video' &&
    creation.duration !== undefined &&
    task.type !== 'music_video' &&
    task.type !== 'lip_sync' &&
    isVideoDurationWithinRange({
      duration: creation.duration,
      maxDuration: Q2_PRO_REFERENCE_VIDEO_MAX_DURATION,
    })
      ? [ActionButtonType.QUICK_EDIT]
      : []),
    ...(canSubmitCreation && creationCardCount === 1 && isLgScreen ? [ActionButtonType.SUBMIT] : []),
    ActionButtonType.DOWNLOAD,
    ...(resolution !== '4k' ? [ActionButtonType.UPSCALE] : []),
    ActionButtonType.FRAME_SHOT,
    // 视频 && 非模版 && 非 music_video 才支持 video2extend
    ...(creation.type === 'video' && !isSceneTask(task.type) && task.type !== 'music_video' && task.type !== 'lip_sync'
      ? [ActionButtonType.EXTEND]
      : []),
    ActionButtonType.SHARE,
    ActionButtonType.ELLIPSIS_MENU,
    ActionButtonType.STAR,
  ];
};

/**
 * 图片任务按钮配置
 */
export const getImageButtonConfig = (options: {
  task: TaskInfo;
  creation?: CreationInfo;
  isLgScreen: boolean;
  type: 'task' | 'creation';
}): ActionButtonType[] => {
  const { task, creation, isLgScreen, type } = options;

  // 生成物上的展示，在失败任务或者没有 creation 都不展示按钮，如果生成物有错误，则展示 ellipsis 菜单
  if (type === 'creation') {
    if (isFailedOrCanceledTask(task) || !creation) {
      return [];
    }

    // 只会出现在 taskCard 下面，而不会出现在 creationCard 上面
    if (creation.err_info?.err_code) {
      return [ActionButtonType.ELLIPSIS_MENU];
    }
  } else {
    // 在任务上展示，在失败任务或者生成物有错误，则展示 ellipsis 菜单
    if (isFailedOrCanceledTask(task) || !creation || creation?.err_info?.err_code) {
      return [ActionButtonType.ELLIPSIS_MENU];
    }
  }

  const creationCardCount = task.settings?.sample_count ?? 1;

  const resolution = task.settings?.resolution;

  return [
    ActionButtonType.QUICK_EDIT,
    ActionButtonType.TRANSFORM_TO_ELEMENT,
    ...(creationCardCount === 1 && isLgScreen && !isAdmissionTemplateTask(task) ? [ActionButtonType.SUBMIT] : []),
    ActionButtonType.DOWNLOAD,
    ...(resolution !== '4k' ? [ActionButtonType.UPSCALE] : []),
    ActionButtonType.SHARE,
    ActionButtonType.ELLIPSIS_MENU,
    ActionButtonType.STAR,
    // 图片任务不支持超分、截帧
  ];
};
